import { useAuthorizedRequest } from '@/hooks/useRequest';
import { apiRequest } from '@/lib/api';
import { CameFrom } from '@/lib/Client';
import type { Client, ClientAttributes } from '@/lib/Client';
import type { Lesson } from '@/lib/Lesson';
import type { Payment } from '@/lib/Payment';
import { t } from '@/lib/i18n';
import { Box, Button, CloseButton, Drawer, HStack, Input, Menu, Portal, Skeleton, Table, Text, Tooltip, VStack } from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type SortCriteria = 'created_at' | 'updated_at' | 'balance';

const sortLabels: Record<SortCriteria, string> = {
    created_at: t('clients.sort_creation'),
    updated_at: t('clients.sort_update'),
    balance: t('clients.sort_balance'),
};

const CAME_FROM_OPTIONS: CameFrom[] = [
    CameFrom.Advertisement, CameFrom.Instagram, CameFrom.Client,
    CameFrom.Manager, CameFrom.Teacher, CameFrom.Website, CameFrom.Search, CameFrom.Other,
];

const cameFromLabels: Record<CameFrom, string> = {
    [CameFrom.Advertisement]: t('came_from.advertisement'),
    [CameFrom.Instagram]: t('came_from.instagram'),
    [CameFrom.Client]: t('came_from.client'),
    [CameFrom.Manager]: t('came_from.manager'),
    [CameFrom.Teacher]: t('came_from.teacher'),
    [CameFrom.Website]: t('came_from.website'),
    [CameFrom.Search]: t('came_from.search'),
    [CameFrom.Other]: t('came_from.other'),
};

function calcAge(dob: string | undefined): string {
    if (!dob) return '';
    const birth = new Date(dob);
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) {
        age--;
    }
    return `${age} р`;
}

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
}

interface ClientFormDrawerProps {
    title: string;
    initial: Partial<ClientAttributes>;
    open: boolean;
    onClose: () => void;
    onSave: (attrs: ClientAttributes) => Promise<void>;
}

function ClientFormDrawer({ title, initial, open, onClose, onSave }: ClientFormDrawerProps) {
    const [name, setName] = useState(initial.name ?? '');
    const [phone, setPhone] = useState(initial.phone ?? '');
    const [email, setEmail] = useState(initial.email ?? '');
    const [instagram, setInstagram] = useState(initial.instagram ?? '');
    const [telegram, setTelegram] = useState(initial.telegram ?? '');
    const [comment, setComment] = useState(initial.comment ?? '');
    const [cameFrom, setCameFrom] = useState<CameFrom>(initial.came_from ?? CameFrom.Other);
    const [hidden, setHidden] = useState(initial.hidden ?? false);
    const [deleted, setDeleted] = useState(initial.deleted ?? false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    function reset() {
        setName(initial.name ?? '');
        setPhone(initial.phone ?? '');
        setEmail(initial.email ?? '');
        setInstagram(initial.instagram ?? '');
        setTelegram(initial.telegram ?? '');
        setComment(initial.comment ?? '');
        setCameFrom(initial.came_from ?? CameFrom.Other);
        setHidden(initial.hidden ?? false);
        setDeleted(initial.deleted ?? false);
        setError('');
    }

    async function handleSubmit() {
        if (!name.trim()) { setError(t('clients.name_required')); return; }
        setSubmitting(true);
        setError('');
        try {
            await onSave({
                name: name.trim(),
                phone: phone.trim() || undefined,
                email: email.trim() || undefined,
                instagram: instagram.trim() || undefined,
                telegram: telegram.trim() || undefined,
                comment: comment.trim() || undefined,
                came_from: cameFrom,
                hidden,
                deleted,
            });
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Unknown error');
        } finally {
            setSubmitting(false);
        }
    }

    function handleOpenChange(e: { open: boolean }) {
        if (!e.open) { reset(); onClose(); }
    }

    const selectStyle = {
        width: '100%', padding: '4px 8px', borderRadius: '4px',
        border: '1px solid #ccc', fontSize: '14px', background: 'transparent',
    };

    return (
        <Drawer.Root open={open} onOpenChange={handleOpenChange} placement="end">
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner>
                    <Drawer.Content>
                        <Drawer.Header>
                            <Drawer.Title>{title}</Drawer.Title>
                        </Drawer.Header>
                        <Drawer.Body>
                            <VStack gap={3} align="stretch">
                                {error && <Text color="red.500" fontSize="sm">{error}</Text>}
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.name')} *</Text>
                                    <Input size="sm" value={name} onChange={e => setName(e.target.value)} placeholder={t('placeholder.full_name')} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.phone')}</Text>
                                    <Input size="sm" value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('placeholder.phone')} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.email')}</Text>
                                    <Input size="sm" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('placeholder.email')} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.instagram')}</Text>
                                    <Input size="sm" value={instagram} onChange={e => setInstagram(e.target.value)} placeholder={t('placeholder.instagram')} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.telegram')}</Text>
                                    <Input size="sm" value={telegram} onChange={e => setTelegram(e.target.value)} placeholder={t('placeholder.telegram')} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.comment')}</Text>
                                    <Input size="sm" value={comment} onChange={e => setComment(e.target.value)} placeholder={t('placeholder.comment')} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb="1">{t('label.came_from')} *</Text>
                                    <select value={cameFrom} onChange={e => setCameFrom(e.target.value as CameFrom)} style={selectStyle}>
                                        {CAME_FROM_OPTIONS.map(o => <option key={o} value={o}>{cameFromLabels[o]}</option>)}
                                    </select>
                                </Box>
                                <Box>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                        <input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} />
                                        {t('label.hidden')}
                                    </label>
                                </Box>
                                <Box>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                        <input type="checkbox" checked={deleted} onChange={e => setDeleted(e.target.checked)} />
                                        {t('label.deleted')}
                                    </label>
                                </Box>
                            </VStack>
                        </Drawer.Body>
                        <Drawer.Footer>
                            <HStack>
                                <Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button>
                                <Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
                            </HStack>
                        </Drawer.Footer>
                        <Drawer.CloseTrigger asChild>
                            <CloseButton size="xs" variant="solid" />
                        </Drawer.CloseTrigger>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
}

export function Clients() {
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const [filter, setFilter] = useState('');
    const [sortCriteria, setSortCriteria] = useState<SortCriteria>('created_at');
    const [sortAsc, setSortAsc] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    const { data: clientsData, error, isLoading, isAuthenticated, mutate: mutateClients } =
        useAuthorizedRequest<{ clients: Client[] }>('/clients?populate=students', jsonFetcher);
    const { data: paymentsData } = useAuthorizedRequest<{ payments: Payment[] }>('/payments', jsonFetcher);
    const { data: lessonsData } = useAuthorizedRequest<{ lessons: Lesson[] }>('/lessons', jsonFetcher);

    const balanceMap = useMemo(() => {
        const map: Record<string, number> = {};
        for (const p of paymentsData?.payments ?? []) {
            if (!p.deleted) map[p.client_id] = (map[p.client_id] ?? 0) + p.amount;
        }
        for (const l of lessonsData?.lessons ?? []) {
            if (!l.deleted) {
                const clientId = l.student_id.split(':')[0]!;
                map[clientId] = (map[clientId] ?? 0) - l.price_uah;
            }
        }
        return map;
    }, [paymentsData, lessonsData]);

    const clients = useMemo(() => {
        const all = clientsData?.clients?.filter(c => {
            const searchFields = [
                c.id, c.name, c.email, c.phone, c.telegram, c.instagram, c.comment,
                ...(c.students?.map(s => s.name) ?? []),
                ...(c.students?.map(s => s.comment) ?? []),
            ].filter(Boolean) as string[];
            return filter === '' || searchFields.some(s => s.toLowerCase().includes(filter.toLowerCase()));
        }) ?? [];

        return all.sort((a, b) => {
            let av: string | number, bv: string | number;
            if (sortCriteria === 'balance') {
                av = balanceMap[a.id] ?? 0;
                bv = balanceMap[b.id] ?? 0;
            } else {
                av = a[sortCriteria];
                bv = b[sortCriteria];
            }
            if (av < bv) return sortAsc ? -1 : 1;
            if (av > bv) return sortAsc ? 1 : -1;
            return 0;
        });
    }, [clientsData, filter, sortCriteria, sortAsc, balanceMap]);

    async function handleCreateClient(attrs: ClientAttributes) {
        const token = await getAccessTokenSilently();
        const result = await apiRequest<{ client: { id: string } }>('POST', '/clients', token, attrs);
        await mutateClients();
        if (result?.client?.id) {
            navigate(`/client/${result.client.id}`);
        }
    }

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (error) return <div>{t('label.error')}: {(error as Error).message}</div>;

    return (
        <VStack gap={4} align="stretch">
            <HStack>
                <Input
                    placeholder={t('clients.search')}
                    variant="subtle"
                    flex="1"
                    onChange={(e) => setFilter(e.target.value)}
                />
                <Menu.Root>
                    <Menu.Trigger asChild>
                        <Button size="sm" variant="outline">{sortLabels[sortCriteria]} ▾</Button>
                    </Menu.Trigger>
                    <Portal>
                        <Menu.Positioner>
                            <Menu.Content>
                                {(Object.keys(sortLabels) as SortCriteria[]).map(k => (
                                    <Menu.Item key={k} value={k} onClick={() => setSortCriteria(k)}>
                                        {sortLabels[k]}
                                    </Menu.Item>
                                ))}
                            </Menu.Content>
                        </Menu.Positioner>
                    </Portal>
                </Menu.Root>
                <Button size="sm" variant="outline" onClick={() => setSortAsc(v => !v)}>
                    {sortAsc ? t('clients.sort_asc') : t('clients.sort_desc')}
                </Button>
                <Button size="sm" onClick={() => setCreateOpen(true)}>{t('clients.new')}</Button>
            </HStack>
            <Box overflowX="auto">
                <Table.Root variant="line" showColumnBorder interactive size="sm">
                    <Table.Header>
                        <Table.Row>
                            <Table.ColumnHeader>{t('label.name')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.links')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.students')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.balance_available')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('label.actions')}</Table.ColumnHeader>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {isLoading ? (
                            [...Array(5)].map((_, i) => (
                                <Table.Row key={i}>
                                    {[...Array(5)].map((_, j) => (
                                        <Table.Cell key={j}><Skeleton height="20px" /></Table.Cell>
                                    ))}
                                </Table.Row>
                            ))
                        ) : clients.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={5} style={{ textAlign: 'center' }}>
                                    <Text color="gray.500">{t('clients.no_clients')}</Text>
                                </Table.Cell>
                            </Table.Row>
                        ) : clients.map((c) => (
                            <Table.Row key={c.id}>
                                <Table.Cell>{c.name}</Table.Cell>
                                <Table.Cell>
                                    <VStack align="start" gap={0}>
                                        {c.email && <Text fontSize="xs"><a href={`mailto:${c.email}`}>{c.email}</a></Text>}
                                        {c.phone && <Text fontSize="xs">{c.phone}</Text>}
                                        {c.telegram && <Text fontSize="xs">tg: {c.telegram}</Text>}
                                        {c.instagram && <Text fontSize="xs">ig: {c.instagram}</Text>}
                                    </VStack>
                                </Table.Cell>
                                <Table.Cell>
                                    <VStack align="start" gap={0}>
                                        {c.students?.map(s => (
                                            <Text key={s.id} fontSize="xs">
                                                {s.name}{s.date_of_birth ? ` (${calcAge(s.date_of_birth)})` : ''}
                                            </Text>
                                        ))}
                                    </VStack>
                                </Table.Cell>
                                <Table.Cell>
                                    {paymentsData ? `${balanceMap[c.id] ?? 0} ₴` : '—'}
                                </Table.Cell>
                                <Table.Cell>
                                    <HStack gap={1}>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Button size="xs" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/client/${c.id}`); }} aria-label={t('client_detail.add_payment')}>💰</Button>
                                            </Tooltip.Trigger>
                                            <Tooltip.Positioner>
                                                <Tooltip.Content>{t('client_detail.add_payment')}</Tooltip.Content>
                                            </Tooltip.Positioner>
                                        </Tooltip.Root>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Button size="xs" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/client/${c.id}`); }} aria-label={t('action.edit')}>✏️</Button>
                                            </Tooltip.Trigger>
                                            <Tooltip.Positioner>
                                                <Tooltip.Content>{t('action.edit')}</Tooltip.Content>
                                            </Tooltip.Positioner>
                                        </Tooltip.Root>
                                    </HStack>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Root>
            </Box>

            <ClientFormDrawer
                title={t('clients.new').replace('+ ', '')}
                initial={{}}
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSave={handleCreateClient}
            />
        </VStack>
    );
}

